import { asciiDateiname } from "../../lib/speicher";
import { attachmentEInvoiceFields, EingabeFehler, isXmlUpload, prepareUpload, serverPruefen, xmlUploadProblem } from "../../model/e-rechnung";
import type { Api } from "../../data";
import type { CreatorScope, CPayout, CPreferences, IgConsent } from "./data";

/**
 * Schreibwege des Creator-Portals. Jede Funktion bekommt den Anschluss, damit die Tests genau diese Wege gegen die
 * Demo-Datenbank prüfen. Rechte prüft die Datenbank (guard_invoice, Spaltenrechte, RLS, Speicher-Policies);
 * hier stehen nur Reihenfolge und Pfadregeln.
 */

// ── Beiträge (0068) ─────────────────────────────────────────────────────────

/**
 * Entwurf eines Beitrags einreichen: derselbe Einreichungsweg wie im Team; Dateien aus der Content-Ablage (0155, vorher
 * hochgeladen), aus dem eigenen Chat oder ein Link bzw. Hinweis.
 */
export async function submitDeliverableDraft(api: Api, input: { deliverableId: string; caption: string; attachmentIds: readonly string[]; mediaIds?: readonly string[] }): Promise<{ version: number; submission_id: string; period_id: string }> {
  return api.rpc("deliverable_submit", { p_deliverable: input.deliverableId, p_caption: input.caption.trim() || null, p_attachments: [...input.attachmentIds],
    ...(input.mediaIds?.length ? { p_media: [...input.mediaIds] } : {}) });
}

/** Beitrag ist online: Link und Datum der Veröffentlichung melden (nie in der Zukunft). */
export async function reportDeliverableLive(api: Api, input: { deliverableId: string; url: string; publishedOn: string }): Promise<{ usage_rights_until: string | null }> {
  return api.rpc("deliverable_report_live", { p_deliverable: input.deliverableId, p_url: input.url.trim(), p_published_on: input.publishedOn });
}

// ── Rechnungen ──────────────────────────────────────────────────────────────

/** Positionen für invoice_save_talent_draft: nur die Felder der Position (Agentur, Rechnung und Reihenfolge setzt die Datenbank) */
export function draftLinePayload(rows: readonly Record<string, unknown>[]): Record<string, unknown>[] {
  return rows.map((l) => ({ description: l.description, quantity: l.quantity, unit_cents: l.unit_cents, tax_rate_bp: l.tax_rate_bp,
    net_cents: l.net_cents, commission_free: l.commission_free === true }));
}

/**
 * Neue Rechnung als Entwurf anlegen oder einen bearbeitbaren Entwurf bzw. eine zurückgegebene Rechnung ändern.
 * Kopf und Positionen in einer Transaktion (invoice_save_talent_draft, 0189; wie invoice_save_draft im Team): scheitert ein
 * Schritt, bleibt der vorige Stand, nie ein Entwurf ohne Positionen (Gesamtprüfung 07.10.2026). Art, Creator und Agentur
 * wechseln nie; eine zurückgegebene Rechnung wird beim Bearbeiten wieder zum Entwurf.
 */
export async function saveInvoiceDraft(
  api: Api,
  scope: CreatorScope,
  input: { invoiceId: string | null; header: Record<string, unknown>; lines: (invoiceId: string) => Record<string, unknown>[]; userId: string | null },
): Promise<string> {
  const id = await api.rpc<string>("invoice_save_talent_draft", {
    p_invoice: input.invoiceId,
    p_header: { ...input.header, talent_id: scope.talentId },
    p_lines: draftLinePayload(input.lines(input.invoiceId ?? "")),
  });
  if (!id) throw new Error("Der Entwurf wurde nicht angelegt.");
  return id;
}

export const ALLOWED_UPLOAD_TYPES = ["application/pdf", "image/jpeg", "image/png"] as const;
/** Grenze des Buckets `documents` (0013): 15 MB */
export const MAX_UPLOAD_BYTES = 15_728_640;

export function safeFileName(name: string): string {
  const cleaned = name.normalize("NFC").replace(/[\\/:*?"<>|\u0000-\u001f]+/g, "-").replace(/\s+/g, " ").trim();
  return (cleaned || "rechnung.pdf").slice(-120);
}

/**
 * Datei prüfen; Meldung in der Sprache des Portals (Befund 2, ohne Angabe Deutsch; der Creator-Rahmen übersetzt sie sonst).
 * Steuerprüfung L12: auch E-Rechnungen als XML (XRechnung, bis 5 MB); ZUGFeRD/Factur-X ist ein PDF.
 */
export function checkUpload(file: { size: number; type: string; name?: string | null }, lang: "de" | "en" = "de"): string | null {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  if (isXmlUpload(file)) return xmlUploadProblem(file, lang);
  if (!(ALLOWED_UPLOAD_TYPES as readonly string[]).includes(file.type)) return t("Bitte eine PDF-, JPG- oder PNG-Datei oder eine E-Rechnung (XML) wählen.", "Please choose a PDF, JPG or PNG file or an e-invoice (XML).");
  if (file.size <= 0) return t("Die Datei ist leer.", "The file is empty.");
  if (file.size > MAX_UPLOAD_BYTES) return t("Die Datei ist größer als 15 MB.", "The file is larger than 15 MB.");
  return null;
}

/**
 * Beleg hochladen (Pfadregel 0013: <agentur>/invoice/<rechnung>/<datei>), Zeile in attachments anlegen und – für die
 * Rechnung selbst – als Rechnungs-PDF eintragen. Nur solange die Rechnung ein Entwurf ist oder korrigiert wird (Speicher-Policy).
 */
export async function attachInvoiceFile(api: Api, scope: CreatorScope, invoiceId: string, file: Blob, fileName: string, asInvoice = true, origin: "uploaded" | "generated" = "uploaded",
  opts: { lang?: "de" | "en"; replaces?: boolean; onWarning?: (text: string) => void } = {}): Promise<string> {
  const problem = checkUpload({ size: file.size, type: file.type, name: fileName }, opts.lang);
  if (problem) throw new EingabeFehler(problem);
  // L12: E-Rechnung (XML, ZUGFeRD) lesen; das Original geht unverändert mit Prüfsumme in die Ablage
  const prepared = await prepareUpload(file, fileName, opts.lang, origin);
  if (prepared.warning) opts.onWarning?.(prepared.warning);
  const name = safeFileName(fileName);
  // Speicherpfad in ASCII (Praxistest G-022: Umlaute lehnt der Speicher ab); der Anzeigename bleibt wie hochgeladen
  const stored = asciiDateiname(name, "beleg").replace(/\.{2,}/g, ".");
  const path = `${scope.orgId}/invoice/${invoiceId}/${Date.now()}-${stored}`;
  await api.upload("documents", path, prepared.blob);
  // Reihenfolge (0044): hochladen, Anlage eintragen, erst dann auf die Datei als Rechnungs-PDF zeigen
  const rows = await api.insert<{ id: string }>("attachments", {
    org_id: scope.orgId, owner_kind: "invoice", owner_id: invoiceId, storage_path: path, file_name: name, mime_type: prepared.mime, size_bytes: file.size,
    category: asInvoice ? "invoice" : "receipt", ...attachmentEInvoiceFields(prepared),
  });
  // E-1 (0448): Prüfsumme und Kurzfassung bildet der Server selbst; scheitert das, bleibt der Upload
  await serverPruefen(api, { attachment: rows?.[0]?.id, path }, prepared);
  // Zusätzliche XRechnung zur erzeugten Rechnung (L12): hängt als Rechnungsdatei an, die erzeugte PDF bleibt die Rechnung im Portal
  if (opts.replaces === false) return path;
  // Die Rechnung selbst wird als Rechnungs-PDF eingetragen (mit Herkunft, 0045); weitere Belege (Hotel, Fahrt …) hängen nur an.
  // Auch hier gilt: eine zurückgegebene Rechnung ist mit dem neuen Beleg wieder ein Entwurf.
  await api.update("invoices", [["id", "eq", invoiceId], ["talent_id", "eq", scope.talentId]], asInvoice ? { pdf_path: path, pdf_origin: origin, status: "draft" } : { status: "draft" });
  // Neue Fassung der Rechnung ersetzt die alte: eine Rechnungsdatei, die die Agentur nie eingereicht bekam (seit der letzten
  // Einreichung angehängt), hängt nicht als „weiterer Beleg“ mit gleicher Nummer und altem Betrag an der Rechnung (weich entfernt,
  // attachment_delete; SICHT-CREATOR-06). Schon eingereichte Fassungen bleiben als Nachweis der Korrektur stehen.
  if (asInvoice) {
    const [inv] = await api.select<{ submitted_at: string | null }>("invoices", { columns: "submitted_at", filters: [["id", "eq", invoiceId]] });
    const old = await api.select<{ id: string; storage_path: string; created_at: string }>("attachments", {
      columns: "id, storage_path, created_at", filters: [["owner_kind", "eq", "invoice"], ["owner_id", "eq", invoiceId], ["category", "eq", "invoice"]],
    });
    for (const a of old) if (a.storage_path !== path && neverSubmitted(a.created_at, inv?.submitted_at ?? null)) await removeInvoiceFile(api, a.id);
  }
  return path;
}

/** Anlage kam nach der letzten Einreichung dazu (oder die Rechnung war nie eingereicht): die Agentur hat sie nie eingereicht bekommen. */
export function neverSubmitted(attachedAt: string, submittedAt: string | null): boolean {
  return !submittedAt || Date.parse(attachedAt) > Date.parse(submittedAt);
}

/** Eigenen Beleg vom Entwurf entfernen (weich; nur solange die Rechnung bearbeitet werden darf, prüft die Datenbank). */
export async function removeInvoiceFile(api: Pick<Api, "rpc">, attachmentId: string): Promise<true> {
  await api.rpc("attachment_delete", { p_id: attachmentId });
  return true;
}

/**
 * Beleg nachreichen (PARITAET-4, 0080): an die eingereichte, freigegebene oder bezahlte Rechnung, ohne sie zu ändern.
 * Die Datenbank lässt nur Belege zu (kein neues Rechnungsdokument), schreibt den Schritt in den Verlauf und meldet ihn dem Team.
 */
export async function addLateReceipt(api: Api, scope: CreatorScope, invoiceId: string, file: Blob, fileName: string, lang: "de" | "en" = "de"): Promise<string> {
  const problem = checkUpload({ size: file.size, type: file.type, name: fileName }, lang);
  if (problem) throw new EingabeFehler(problem);
  const prepared = await prepareUpload(file, fileName, lang);
  const name = safeFileName(fileName);
  // Speicherpfad in ASCII (Praxistest G-022: Umlaute lehnt der Speicher ab); der Anzeigename bleibt wie hochgeladen
  const stored = asciiDateiname(name, "beleg").replace(/\.{2,}/g, ".");
  const path = `${scope.orgId}/invoice/${invoiceId}/${Date.now()}-${stored}`;
  await api.upload("documents", path, prepared.blob);
  await api.insert("attachments", {
    org_id: scope.orgId, owner_kind: "invoice", owner_id: invoiceId, storage_path: path, file_name: name, mime_type: prepared.mime, size_bytes: file.size,
    category: "receipt", ...attachmentEInvoiceFields(prepared),
  }, { returning: false });
  await serverPruefen(api, { path }, prepared);
  return path;
}

/** Nach dem Einreichen darf der Creator Belege nachreichen (nicht im Entwurf, nicht zurückgezogen oder verworfen; 0080) */
export function canAddLateReceipt(row: { kind: string; status: string; credit_for?: string | null }): boolean {
  // K-448: an einen Stornobeleg gehört kein nachgereichter Beleg
  return (row.kind === "talent_to_agency" || row.kind === "talent_to_brand") && !row.credit_for && ["submitted", "approved", "sent", "paid", "cancelled"].includes(row.status);
}

/**
 * Einreichen: Status „eingereicht“ (der Wächter stempelt den Zeitpunkt, die Datenbank schreibt den Schritt ins Protokoll, 0031).
 * Bei eigener Datei bestätigt der Creator im selben Schritt Nummer und Betrag (pdf_confirmed_at, 0045).
 */
export async function submitInvoice(api: Api, scope: CreatorScope, invoiceId: string, opts: { again: boolean; comment?: string | null; confirmPdf?: boolean; confirmTax?: boolean }): Promise<true> {
  // 0181: bestätigte steuerliche Behandlung (Reverse Charge, nicht steuerbar, manuell) im selben Schritt; den Zeitpunkt setzt die Datenbank
  const now = new Date().toISOString();
  await api.update("invoices", [["id", "eq", invoiceId], ["talent_id", "eq", scope.talentId]], {
    ...(opts.confirmPdf ? { pdf_confirmed_at: now } : {}),
    ...(opts.confirmTax ? { tax_treatment_confirmed_at: now } : {}),
    status: "submitted",
  });
  const comment = opts.comment?.trim();
  // Den Verlauf liest der Creator über talent_invoice_events (0037); ohne Leserecht auf die Tabelle nichts zurücklesen
  if (comment) await api.insert("invoice_events", { org_id: scope.orgId, invoice_id: invoiceId, event: "comment", details: { text: comment.slice(0, 2000) } }, { returning: false });
  return true;
}

/** Einreichung zurücknehmen, solange die Agentur noch nicht geprüft hat: die Rechnung ist wieder ein Entwurf (RE-18, 0031). */
export async function withdrawSubmission(api: Api, invoiceId: string): Promise<{ status: string }> {
  return api.rpc<{ status: string }>("invoice_withdraw_submission", { p_invoice: invoiceId });
}

/** Eigene Zahlungsmeldung zurücknehmen, solange die Agentur nicht gebucht hat (TA-066, 0031). */
export async function retractPaymentReport(api: Api, invoiceId: string): Promise<true> {
  await api.rpc("invoice_retract_payment_report", { p_invoice: invoiceId });
  return true;
}

/** Einer Gutschrift der Agentur widersprechen (0046/0051): sie verliert ihre Wirkung als Rechnung, die Agentur bekommt eine Aufgabe. */
export async function objectSelfBilling(api: Api, invoiceId: string, reason: string): Promise<{ status: string }> {
  return api.rpc<{ status: string }>("invoice_object_self_billing", { p_invoice: invoiceId, p_reason: reason.trim() });
}

/** Gutschrift als geprüft bestätigen („Passt so“, CREATOR-R3-5, 0091); der Widerspruch bleibt bis zur Auszahlung möglich. */
export async function acceptSelfBilling(api: Api, invoiceId: string): Promise<true> {
  await api.rpc("invoice_accept_self_billing", { p_invoice: invoiceId });
  return true;
}

/** Überweisung an die Agentur melden (Rechnung der Agentur an den Creator, CREATOR-R3-6, 0091); die Agentur erfasst den Eingang. */
export async function reportTransfer(api: Api, invoiceId: string): Promise<{ status: string }> {
  return api.rpc<{ status: string }>("invoice_report_payment", { p_invoice: invoiceId, p_response: "paid" });
}

/** Bei der Agentur nachfragen (Auszahlung überfällig): Chat-Nachricht mit Verweis auf die Rechnung (Befund 11). */
export async function askAgencyAboutInvoice(api: Api, talentId: string, invoiceId: string, body: string): Promise<true> {
  const text = body.trim();
  if (!text) throw new Error("Bitte schreib kurz, worum es geht.");
  await api.rpc("chat_send", { p_talent: talentId, p_body: text.slice(0, 4000), p_link_kind: "invoice", p_link_id: invoiceId, p_side: "talent" });
  return true;
}

/**
 * CREATOR-6: Nachfrage zu einer offenen Auszahlung bei einer früheren Agentur (archiviertes Profil, Chat geschlossen):
 * Aufgabe mit Mail an deren Team (0079). Die Antwort kommt per Mail an deine Anmeldeadresse.
 */
export async function askFormerAgencyAboutPayout(api: Api, invoiceId: string, body: string): Promise<true> {
  const text = body.trim();
  if (text.length < 5) throw new Error("Bitte schreib kurz, worum es geht.");
  await api.rpc("talent_archived_payout_ask", { p_invoice: invoiceId, p_message: text.slice(0, 1000) });
  return true;
}

/** Name, Antwortadresse und Telefon einer (auch früheren) Agentur (0079) */
export interface AgencyContact { name?: string; email?: string; phone?: string }
export async function loadAgencyContact(api: Api, orgId: string): Promise<AgencyContact | null> {
  return (await api.read<AgencyContact | null>("my_agency_contact", { p_org: orgId }).catch(() => null)) ?? null;
}

/** Zurückgegebene Rechnung zurückziehen: sie wird nie wirksam und gibt ihre Nummer frei (WS-06). */
export async function withdrawInvoice(api: Api, scope: CreatorScope, invoiceId: string): Promise<true> {
  await api.update("invoices", [["id", "eq", invoiceId], ["talent_id", "eq", scope.talentId]], { status: "withdrawn" });
  return true;
}

/** Entwurf verwerfen: weich, nie löschen (RE-17). */
export async function discardInvoice(api: Api, scope: CreatorScope, invoiceId: string): Promise<true> {
  await api.update("invoices", [["id", "eq", invoiceId], ["talent_id", "eq", scope.talentId]], { status: "discarded" });
  return true;
}

/** „Geld ist angekommen“ oder „noch nicht angekommen“ melden (RE-25, TA-066/067). */
export async function reportPayment(api: Api, invoiceId: string, response: "paid" | "still_overdue"): Promise<{ status: string }> {
  return api.rpc<{ status: string }>("invoice_report_payment", { p_invoice: invoiceId, p_response: response });
}

/** Datei aus dem privaten Bucket holen (Lesen nur mit Leserecht auf das Objekt). */
export function fetchDocument(api: Api, path: string): Promise<Blob> {
  return api.download("documents", path);
}

// ── Profil ──────────────────────────────────────────────────────────────────

/**
 * Spalten, die der Creator am eigenen Datensatz ändern darf (Provision, Status, Nummer, Agentur sind tabu). Dieselbe
 * Positivliste prüft die Datenbank (app.talent_self_fields, 0092, KETTE-11), dort zusätzlich Sprache und Mail-Einstellungen.
 */
export const TALENT_SELF_FIELDS = [
  "first_name", "last_name", "display_name", "nickname", "email", "street", "postal_code", "city", "country", "vat_id", "tax_number",
  "small_business", "company_name", "legal_form", "register_info", "instagram_handle", "tiktok_handle", "youtube_handle", "whatsapp_phone", "whatsapp_opt_in_at", "profile_image_path",
  "invoice_logo",
] as const;
export type TalentSelfField = (typeof TALENT_SELF_FIELDS)[number];

export async function saveTalentProfile(api: Api, scope: CreatorScope, patch: Partial<Record<TalentSelfField, unknown>>): Promise<true> {
  const clean = Object.fromEntries(Object.entries(patch).filter(([k]) => (TALENT_SELF_FIELDS as readonly string[]).includes(k)));
  if (!Object.keys(clean).length) return true;
  await api.update("talents", [["id", "eq", scope.talentId], ["org_id", "eq", scope.orgId]], clean);
  return true;
}

export async function savePayout(api: Api, scope: CreatorScope, patch: Omit<CPayout, "talent_id" | "org_id">, exists: boolean): Promise<true> {
  if (exists) await api.update("talent_payout_details", [["talent_id", "eq", scope.talentId]], patch);
  else await api.insert("talent_payout_details", { talent_id: scope.talentId, org_id: scope.orgId, ...patch });
  return true;
}

/** Bild im eigenen Ordner ablegen (Pfadregel 0018: <agentur>/talent/<talent>/<datei>). */
export async function uploadTalentImage(api: Api, scope: CreatorScope, file: Blob, prefix: "unterschrift" | "profilbild"): Promise<string> {
  if (!["image/png", "image/jpeg"].includes(file.type)) throw new Error("Bitte ein PNG- oder JPG-Bild wählen.");
  if (file.size <= 0 || file.size > MAX_UPLOAD_BYTES) throw new Error("Das Bild ist leer oder größer als 15 MB.");
  const path = `${scope.orgId}/talent/${scope.talentId}/${prefix}-${Date.now()}.${file.type === "image/png" ? "png" : "jpg"}`;
  await api.upload("documents", path, file);
  return path;
}

/** Gespeicherte Unterschrift (TA-106): nur das Talent selbst sieht sie. */
export async function saveSignature(api: Api, scope: CreatorScope, signingName: string, image: Blob | null, exists: boolean): Promise<true> {
  const name = signingName.trim();
  if (name.length < 2) throw new Error("Bitte deinen vollständigen Namen für die Unterschrift angeben.");
  if (!image && !exists) throw new Error("Bitte zuerst eine Unterschrift zeichnen oder ein Bild wählen.");
  const patch: Record<string, unknown> = { signing_name: name };
  if (image) patch.image_path = await uploadTalentImage(api, scope, image, "unterschrift");
  if (exists) await api.update("talent_signatures", [["talent_id", "eq", scope.talentId]], patch);
  else await api.insert("talent_signatures", { talent_id: scope.talentId, org_id: scope.orgId, ...patch });
  return true;
}

export async function saveProfileImage(api: Api, scope: CreatorScope, image: Blob | null): Promise<true> {
  const path = image ? await uploadTalentImage(api, scope, image, "profilbild") : null;
  await saveTalentProfile(api, scope, { profile_image_path: path });
  return true;
}

/** Sprache auch an den Profilen hinterlegen, damit Mails der Agenturen sie nutzen (talents.language, 0041). */
export async function saveTalentLanguage(api: Api, talentIds: readonly string[], language: "de" | "en"): Promise<true> {
  for (const id of new Set(talentIds)) await api.rpc("talent_set_preferences", { p_talent: id, p_language: language });
  return true;
}

/** Angaben aus dem Profil bei einer anderen Agentur (Befund 13): nur eigene, aktive Profile sind lesbar. */
export async function loadOtherProfiles(api: Api, talentIds: readonly string[]): Promise<{ talent: Record<string, unknown>; payout: Record<string, unknown> | null }[]> {
  if (!talentIds.length) return [];
  const ids = [...talentIds];
  const [talents, payouts] = await Promise.all([
    api.select<Record<string, unknown> & { id: string }>("talents", { filters: [["id", "in", ids]] }),
    api.select<Record<string, unknown> & { talent_id: string }>("talent_payout_details", { filters: [["talent_id", "in", ids]] }),
  ]);
  return talents.map((t) => ({ talent: t, payout: payouts.find((p) => p.talent_id === t.id) ?? null }));
}

/** Steuerangaben in den Profilen bei weiteren Agenturen übernehmen (Kleinunternehmer, USt-IdNr., Steuernummer). */
export async function saveTaxToProfiles(api: Api, profiles: readonly { talentId: string; orgId: string }[], patch: { small_business?: boolean; vat_id: string | null; tax_number: string | null; country?: string | null }): Promise<true> {
  for (const p of profiles) await saveTalentProfile(api, { talentId: p.talentId, orgId: p.orgId }, patch);
  return true;
}

/** Steuerstatus mit Gültig-ab (0062): Verlauf; der Schalter im Profil folgt am Stichtag, Belege nehmen den Stand zum Belegdatum. */
export async function setTaxStatus(api: Api, p: { orgId: string; talentId: string; smallBusiness: boolean; validFrom: string; note?: string | null }): Promise<true> {
  await api.rpc("tax_status_set", { p_org: p.orgId, p_talent: p.talentId, p_small_business: p.smallBusiness, p_valid_from: p.validFrom, p_note: p.note ?? null });
  return true;
}

export interface TaxStatusEntry { small_business: boolean; valid_from: string; note: string | null }
export async function loadTaxHistory(api: Api, talentId: string): Promise<TaxStatusEntry[]> {
  return api.select<TaxStatusEntry>("tax_status_history", { columns: "small_business, valid_from, note", filters: [["talent_id", "eq", talentId]], order: [["valid_from", "asc"]] })
    .catch(() => [] as TaxStatusEntry[]);
}

export async function savePreferences(api: Api, userId: string, patch: Partial<Omit<CPreferences, "user_id">>, exists: boolean): Promise<true> {
  if (exists) await api.update("user_preferences", [["user_id", "eq", userId]], patch);
  else await api.insert("user_preferences", { user_id: userId, ...patch });
  return true;
}

/**
 * CREATOR-R2-9: Die Sprache, in der jemand die Einladung angenommen hat, wird Vorgabe fürs Portal, solange noch keine eigene
 * Wahl gespeichert ist. Mails folgen ihr über app.talent_language, solange die Agentur keine andere Sprache am Profil hat.
 */
export async function adoptInvitationLanguage(api: Api, userId: string, language: "de" | "en"): Promise<boolean> {
  const own = await api.select<{ user_id: string; language: string | null }>("user_preferences", { columns: "user_id, language", filters: [["user_id", "eq", userId]] });
  if (own[0]?.language) return false;
  await savePreferences(api, userId, { language }, own.length > 0);
  return true;
}

// ── Instagram ───────────────────────────────────────────────────────────────

export const IG_BLOCKS: { key: string; label: string; en: string }[] = [
  { key: "profile", label: "Profil (Name, Follower, Beiträge)", en: "Profile (name, followers, posts)" },
  { key: "reach", label: "Reichweite", en: "Reach" },
  { key: "views", label: "Aufrufe", en: "Views" },
  { key: "interactions", label: "Interaktionen", en: "Interactions" },
  { key: "stories", label: "Story-Aufrufe", en: "Story views" },
  { key: "content_types", label: "Content-Arten", en: "Content types" },
  { key: "follower_split", label: "Follower und Nicht-Follower", en: "Followers and non-followers" },
  { key: "profile_clicks", label: "Profilaufrufe und Klicks", en: "Profile visits and clicks" },
  { key: "top_posts", label: "Top-Beiträge", en: "Top posts" },
  { key: "age", label: "Alter der Zielgruppe", en: "Audience age" },
  { key: "gender", label: "Geschlecht der Zielgruppe", en: "Audience gender" },
  { key: "countries", label: "Länder der Zielgruppe", en: "Audience countries" },
  { key: "cities", label: "Städte der Zielgruppe", en: "Audience cities" },
  // 0340: eigener Block, eigene Zustimmung (bestehende Zustimmungen schließen ihn nicht ein)
  { key: "story_items", label: "Storys einzeln (Reichweite, Aufrufe, Navigation je Story)", en: "Individual stories (reach, views, navigation per story)" },
];

/** Einwilligung zur Weitergabe an Marken (TA-139): das Talent entscheidet, das Team liest. */
export async function saveIgConsent(api: Api, scope: CreatorScope, allowed: boolean, blocks: readonly string[], existing: IgConsent | null): Promise<true> {
  const valid = blocks.filter((b) => IG_BLOCKS.some((x) => x.key === b));
  const patch = { sharing_allowed: allowed, allowed_blocks: allowed ? valid : [] };
  if (existing) await api.update("ig_share_consent", [["talent_id", "eq", scope.talentId]], patch);
  else await api.insert("ig_share_consent", { talent_id: scope.talentId, org_id: scope.orgId, ...patch });
  return true;
}

export async function disconnectInstagram(api: Api, scope: CreatorScope): Promise<true> {
  await api.rpc("ig_disconnect", { p_talent: scope.talentId });
  return true;
}

export async function revokeIgShare(api: Api, shareId: string): Promise<true> {
  await api.rpc("ig_share_revoke", { p_share: shareId });
  return true;
}

/** Schritt 3 der Verbindung: das Ticket aus dem Rücklauf mit Anmeldung einlösen; die Datenbank stößt den Erst-Abgleich an (0156). */
export async function finishIgConnect(api: Api, ticket: string): Promise<{ username: string; sync?: "started" | "unavailable" }> {
  return api.rpc<{ talent_id: string; username: string; sync?: "started" | "unavailable" }>("ig_connect_finish", { p_ticket: ticket });
}

/** Schritt 1 der Verbindung (0156): Adresse der Instagram-Anmeldung holen; null ohne Meta-App (Demo). */
export async function startIgConnect(api: Api, talentId: string): Promise<string | null> {
  if (!api.instagramConnect) return null;
  return api.instagramConnect(talentId);
}

/** Antwort des Knopfs „Aktualisieren“ (public.ig_sync_request, 0156) */
export interface IgSyncRequest {
  state: "expired" | "first_sync" | "running" | "fresh" | "unavailable" | "started";
  started_at?: string | null;
  last_sync_at?: string | null;
  retry_after?: number;
}
/** Knopf „Aktualisieren“: echte Sperre in der Datenbank (Zeilensperre, 10 Minuten). */
export async function requestIgSync(api: Api, talentId: string): Promise<IgSyncRequest> {
  return api.rpc<IgSyncRequest>("ig_sync_request", { p_talent: talentId });
}

// ── Einladung ───────────────────────────────────────────────────────────────

/** Öffentliche Vorschau einer Einladung (0031): Agentur, Zustand, Art und Adresshinweis, ohne Anmeldung. */
export interface InvitationPreview {
  state: "open" | "expired" | "revoked" | "accepted" | "unknown";
  agency?: string | null;
  kind?: "member" | "talent";
  expires_at?: string | null;
  email_hint?: string | null;
  /** Profil unter 18: Annehmen nur mit Einwilligung der Erziehungsberechtigten (0058) */
  guardian_required?: boolean;
  /** Sprache der Einladungsmail (0097): Vorgabe der Seite, wenn der Link keine Sprache trägt */
  language?: "de" | "en" | null;
}
/**
 * Sprache der Einladungsseite (CREATOR-R3-3): ?sprache= im Link, sonst die Sprache der Einladungsmail aus der Vorschau,
 * sonst Deutsch. Eine ausdrückliche Wahl (Link oder Umschalter) geht immer vor.
 */
export function einladungsSprache(param: string | null, preview: Pick<InvitationPreview, "language"> | null | undefined): "de" | "en" {
  if (param === "en" || param === "de") return param;
  return preview?.language === "en" ? "en" : "de";
}
export async function invitationPreview(api: Api, token: string): Promise<InvitationPreview> {
  const t = token.trim();
  if (t.length < 20) return { state: "unknown" };
  return api.read<InvitationPreview>("invitation_public_view", { p_token: t });
}

/**
 * Zustimmung zu Nutzungsbedingungen und Datenschutzhinweisen nach der Annahme speichern. Die Fassung kommt aus den
 * Plattform-Standards (consent_missing nennt sie), nie aus dem Code (0057, Befund 11).
 */
export async function recordConsent(api: Api): Promise<true> {
  const r = await api.read<{ kind: string; version: string }[] | string | null>("consent_missing");
  const missing = (typeof r === "string" ? (JSON.parse(r) as { kind: string; version: string }[]) : r) ?? [];
  for (const i of missing) {
    if (i.kind === "user_terms" || i.kind === "privacy") await api.rpc("consent_record", { p_kind: i.kind, p_version: i.version });
  }
  return true;
}

/** Stand der Erklärung, die die erziehungsberechtigte Person beim Annehmen abgibt (0058). */
export const GUARDIAN_STATEMENT_VERSION = "2026-09";

/** Einwilligung der erziehungsberechtigten Person festhalten (vor accept_invitation, 0058). */
export async function recordGuardianConsent(api: Api, token: string, guardian: { name: string; email: string }): Promise<true> {
  await api.rpc("invitation_guardian_consent", {
    p_token: token.trim(), p_guardian_name: guardian.name.trim(), p_guardian_email: guardian.email.trim() || null, p_version: GUARDIAN_STATEMENT_VERSION,
  });
  return true;
}

/** Passt die Adresse zur Einladung? (Befund 33: vor dem Anlegen des Kontos prüfen; null = Einladung unbekannt) */
export async function invitationEmailMatches(api: Api, token: string, email: string): Promise<boolean | null> {
  const t = token.trim();
  if (t.length < 20) return null;
  const r = await api.read<InvitationPreview & { email_matches?: boolean | null }>("invitation_public_view", { p_token: t, p_email: email.trim() });
  return r.state === "unknown" ? null : r.email_matches ?? null;
}

export async function acceptInvitation(api: Api, token: string): Promise<{ org_id: string; kind: "member" | "talent" }> {
  const t = token.trim();
  if (t.length < 20) throw new Error("Dieser Einladungslink ist unvollständig. Bitte öffne den Link aus deiner E-Mail vollständig.");
  return api.rpc<{ org_id: string; kind: "member" | "talent" }>("accept_invitation", { p_token: t });
}
